import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { Eye, UserRoundX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_STOP_IMPERSONATE } from '~/gql/auth/auth'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/router'

/**
 * Bandeau affiché en haut de l'application pendant une session d'impersonation
 * (payload.impersonatorId présent). Permet à l'admin de revenir à son propre
 * compte via la mutation stopImpersonate, qui ré-émet ses cookies de session.
 */
function ImpersonationBanner() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { payload, setPayload } = useAuth()

  const [stopImpersonate, { loading }] = useMutation(GQL_STOP_IMPERSONATE)

  if (!payload?.impersonatorId) return null

  const onStop = async () => {
    try {
      const { data } = await stopImpersonate()
      if (data?.stopImpersonate.__typename === 'UserInfo') {
        setPayload({
          firstName: data.stopImpersonate.firstName,
          lastName: data.stopImpersonate.lastName,
          email: data.stopImpersonate.email,
          role: data.stopImpersonate.roles,
          id: data.stopImpersonate.id,
        })
        navigate(getDefaultRoute(data.stopImpersonate.roles as RoleEnum))
      } else {
        // Session admin irrécupérable (compte rétrogradé/désactivé) : on repart de zéro.
        toast.error(t('impersonate.exit.failed'))
        navigate('/logout')
      }
    } catch {
      toast.error(t('impersonate.exit.failed'))
      navigate('/logout')
    }
  }

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <Eye className="size-4 shrink-0" />
      <span className="font-medium">
        {t('impersonate.banner.message', {
          name: `${payload.firstName} ${payload.lastName}`,
        })}
      </span>
      {/*
        Rappel permanent : seul le tracking SCORM est neutralisé pendant
        l'impersonation, le reste des mutations part pour de vrai au nom
        de l'utilisateur consulté.
      */}
      <span className="opacity-80">
        {t('impersonate.banner.warning')}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-400 bg-transparent hover:bg-amber-200 dark:border-amber-600 dark:hover:bg-amber-900"
        disabled={loading}
        onClick={onStop}
      >
        <UserRoundX className="size-4 mr-1" />
        {t('impersonate.banner.exit')}
      </Button>
    </div>
  )
}

export default ImpersonationBanner
